import React, { useEffect, useMemo, useState } from 'react';
import { searchUsers } from '../../../services/collaboratorService';
import './RouteCollaboratorsModal.css';

const roles = [
  { key: 'viewer', label: 'Viewer' },
  { key: 'editor', label: 'Editor' }
];

/**
 * Collaborators modal used in RouteCreation.
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - initialCollaborators: Array<{ user_id, role, username?, full_name?, avatar_url?, profilePicture? }>
 * - onSave: (collaborators) => void
 * - ownerUserId: string (to prevent adding self)
 */
export default function RouteCollaboratorsModal({ open, onClose, initialCollaborators = [], onSave, ownerUserId }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [collaborators, setCollaborators] = useState(initialCollaborators);
  const [error, setError] = useState('');

  useEffect(() => {
    setCollaborators(initialCollaborators || []);
  }, [initialCollaborators]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await searchUsers(query, { limit: 10 });
        if (active) setResults(data || []);
      } catch (e) {
        if (active) setError('Search failed');
      }
      if (active) setLoading(false);
    };
    run();
    return () => { active = false; };
  }, [query]);

  const byId = useMemo(() => new Map((collaborators || []).map(c => [c.user_id, c])), [collaborators]);

  const addUser = (user) => {
    if (!user?.user_id) return;
    if (user.user_id === ownerUserId) return; // don't add self
    if (byId.has(user.user_id)) return; // already added
    setCollaborators(prev => [...prev, { ...user, role: 'viewer' }]);
  };

  const removeUser = (userId) => {
    setCollaborators(prev => prev.filter(c => c.user_id !== userId));
  };

  const setRole = (userId, role) => {
    setCollaborators(prev => prev.map(c => c.user_id === userId ? { ...c, role } : c));
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Invite collaborators</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search by username or name..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="route-name-input"
              style={{ width: '100%' }}
            />
            {error && <div style={{ color: 'crimson', marginTop: 6 }}>{error}</div>}
          </div>
          <div className="collab-columns">
            <div className="collab-col">
              <div className="form-section">
                <div className="section-header" style={{ cursor: 'default' }}>
                  <div className="section-title"><span className="section-icon">🔎</span><span>Results</span></div>
                </div>
                <div className="section-content expanded" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {loading ? (
                    <div style={{ color: '#6b7280' }}>Searching…</div>
                  ) : (results || []).length === 0 ? (
                    <div className="empty-copy">No results</div>
                  ) : (
                    <ul className="collab-list">
                      {results.map(r => (
                        <li key={r.user_id} className="collab-row">
                          <div className="collab-left">
                            <Avatar url={r.avatar_url || r.profilePicture} username={r.username || r.name || r.user_id} />
                            <div className="collab-meta">
                              <strong className="collab-username">{r.username || r.name || 'User'}</strong>
                              <span className="collab-sub">{r.user_id.slice(0, 8)}…</span>
                            </div>
                          </div>
                          <button className="add-btn" onClick={() => addUser(r)} disabled={r.user_id === ownerUserId || byId.has(r.user_id)}>Add</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="collab-col">
              <div className="form-section">
                <div className="section-header" style={{ cursor: 'default' }}>
                  <div className="section-title"><span className="section-icon">👥</span><span>Selected</span></div>
                </div>
                <div className="section-content expanded" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {(collaborators || []).length === 0 ? (
                    <div className="empty-copy">No collaborators yet</div>
                  ) : (
                    <ul className="collab-list">
                      {collaborators.map(c => (
                        <li key={c.user_id} className="collab-row">
                          <div className="collab-left">
                            <Avatar url={c.avatar_url || c.profilePicture} username={c.username || c.name || c.user_id} />
                            <div className="collab-meta">
                              <strong className="collab-username">{c.username || c.name || 'User'}</strong>
                              <span className="collab-sub">{c.user_id.slice(0, 8)}…</span>
                            </div>
                          </div>
                          <div className="collab-actions">
                            <select value={c.role} onChange={e => setRole(c.user_id, e.target.value)} className="role-select">
                              {roles.map(r => (
                                <option value={r.key} key={r.key}>{r.label}</option>
                              ))}
                            </select>
                            <button className="remove-point-btn" onClick={() => removeUser(c.user_id)} title="Remove">×</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="action-bar" style={{ position: 'static', margin: 0, boxShadow: 'none', borderTop: '1px solid #e5e7eb' }}>
          <button className="save-draft-btn" onClick={onClose}>Cancel</button>
          <button className="publish-btn" onClick={() => onSave(collaborators)}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ url, username }) {
  if (url) return <span className="avatar"><img src={url} alt={username || 'user'} /></span>;
  const letter = (username || 'U').toString().charAt(0).toUpperCase();
  return <span className="avatar">{letter}</span>;
}


